import { ColorEnum } from '@/constants/color'
import { ButtonStateEnum } from '@/features/ui/buttons/constants'
import { useEnabledButtonState } from '@/features/ui/buttons/hooks/useEnabledButtonState'
import Typography from '@/features/ui/typography'
import { hexToRgba } from '@/utils/hexToRgba'
import { ComponentType, FC } from 'react'
import { Pressable, StyleProp, TextStyle, View, ViewStyle } from 'react-native'
import { SvgProps } from 'react-native-svg'
import { createStyleSheet, useStyles } from 'react-native-unistyles'

interface ChipProps {
    label: string
    subText?: string
    Icon?: ComponentType<SvgProps>
    enabledTextColor?: string
    labelStyle?: StyleProp<Pick<TextStyle, 'textTransform'>>
    active?: boolean
    disabled?: boolean
    onPress: () => void
    style?: StyleProp<ViewStyle>
}

const Chip: FC<ChipProps> = ({
    label,
    subText,
    enabledTextColor = ColorEnum.JET_900,
    labelStyle,
    disabled,
    Icon,
    onPress,
    active,
    style,
}) => {
    const { state, eventHandlers } = useEnabledButtonState()

    const { styles } = useStyles(stylesheet, {
        active,
        disabled,
        withSubText: !!subText,
    })

    return (
        <Pressable
            {...eventHandlers}
            disabled={disabled}
            onPress={onPress}
            style={[styles.container, styles.extraContainer(state, active), style]}
        >
            {Icon && <Icon color={styles.extraIconAndLabel(enabledTextColor).color} />}
            <View style={styles.textContainer}>
                <Typography.Menu
                    numberOfLines={Icon ? 1 : 2}
                    style={[styles.extraIconAndLabel(enabledTextColor), labelStyle]}
                >
                    {label}
                </Typography.Menu>
                {subText && (
                    <Typography.Description
                        numberOfLines={1}
                        size="small"
                        font="bold"
                        style={styles.extraSubText(enabledTextColor)}
                    >
                        {subText}
                    </Typography.Description>
                )}
            </View>
        </Pressable>
    )
}

const stylesheet = createStyleSheet({
    container: {
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: ColorEnum.VIOLET_200,
        borderRadius: 8,
        paddingHorizontal: 10,
        maxWidth: '100%',
        variants: {
            withSubText: {
                false: {
                    paddingVertical: 6,
                },
            },
        },
    },
    extraContainer: (state: ButtonStateEnum, active: boolean | undefined) => {
        let backgroundColor = ColorEnum.VIOLET_200

        if (state !== ButtonStateEnum.Default) backgroundColor = ColorEnum.Violet300

        if (active) {
            backgroundColor = ColorEnum.YELLOW_600

            if (state !== ButtonStateEnum.Default) backgroundColor = ColorEnum.Yellow700
        }

        return { backgroundColor }
    },
    textContainer: {
        alignItems: 'center',
        maxWidth: '100%',
        paddingVertical: 4,
    },
    extraIconAndLabel: (color: string) => ({
        color,
        textAlign: 'center',
        variants: {
            active: {
                true: {
                    color: ColorEnum.WHITE,
                },
            },
            disabled: {
                true: {
                    color: ColorEnum.GRAY_500,
                },
            },
        },
    }),
    extraSubText: (color: string) => ({
        color,
        variants: {
            active: {
                true: {
                    color: hexToRgba(ColorEnum.WHITE, 0.8),
                },
            },
            disabled: {
                true: {
                    color: ColorEnum.GRAY_500,
                },
            },
        },
    }),
})

export default Chip
